import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  MessageCircle,
  Wifi,
  WifiOff,
  RefreshCcw,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const API_URL = process.env.REACT_APP_API_URL;
const POLL_INTERVAL = 4000; // ms entre polls de estado

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const statusLabel = {
  disconnected: "Desconectado",
  connecting: "Conectando…",
  qr: "Esperando escaneo",
  authenticating: "Autenticando…",
  ready: "Conectado",
};

const statusColor = {
  disconnected: "text-red-500",
  connecting: "text-amber-500",
  qr: "text-blue-500",
  authenticating: "text-amber-500",
  ready: "text-emerald-500",
};

const statusBg = {
  disconnected: "bg-red-50 border-red-200",
  connecting: "bg-amber-50 border-amber-200",
  qr: "bg-blue-50 border-blue-200",
  authenticating: "bg-amber-50 border-amber-200",
  ready: "bg-emerald-50 border-emerald-200",
};

const estadoBadge = {
  enviado: "bg-emerald-100 text-emerald-700",
  error: "bg-red-100 text-red-700",
  pendiente: "bg-amber-100 text-amber-700",
};

const PAGE_SIZE = 15;

// ─────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────
const WhatsAppPage = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // ── Estado de conexión ─────────────────────────────────────
  const [waStatus, setWaStatus] = useState({
    status: "disconnected",
    qr: null,
    phoneNumber: null,
    profileName: null,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const pollRef = useRef(null);

  // ── Historial de envíos ────────────────────────────────────
  const [envios, setEnvios] = useState([]);
  const [totalEnvios, setTotalEnvios] = useState(0);
  const [pageEnvios, setPageEnvios] = useState(1);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [loadingEnvios, setLoadingEnvios] = useState(false);

  // ─────────────────────────────────────────────────────────
  // Poll de estado WhatsApp
  // ─────────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/whatsapp/status`, {
        headers,
      });
      setWaStatus(data);
    } catch (_) {}
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  // ─────────────────────────────────────────────────────────
  // Historial de envíos
  // ─────────────────────────────────────────────────────────
  const fetchEnvios = useCallback(async () => {
    setLoadingEnvios(true);
    try {
      const params = { page: pageEnvios, pageSize: PAGE_SIZE };
      if (filtroTipo) params.tipo = filtroTipo;
      if (filtroEstado) params.estado = filtroEstado;
      const { data } = await axios.get(`${API_URL}/whatsapp/envios`, {
        headers,
        params,
      });
      setEnvios(data.items || []);
      setTotalEnvios(data.total || 0);
    } catch (_) {
      toast.error("Error cargando historial de envíos");
    } finally {
      setLoadingEnvios(false);
    }
  }, [pageEnvios, filtroTipo, filtroEstado]); // eslint-disable-line

  useEffect(() => {
    fetchEnvios();
  }, [fetchEnvios]);

  // ─────────────────────────────────────────────────────────
  // Acciones
  // ─────────────────────────────────────────────────────────
  const handleInitialize = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/whatsapp/initialize`, {}, { headers });
      toast.info("Iniciando WhatsApp, espera el código QR…");
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error iniciando WhatsApp");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("¿Seguro que deseas desconectar WhatsApp?")) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/whatsapp/disconnect`, {}, { headers });
      toast.success("WhatsApp desconectado");
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error desconectando WhatsApp");
    } finally {
      setActionLoading(false);
    }
  };

  const isConnected = waStatus.status === "ready";
  const isConnecting = ["connecting", "authenticating"].includes(
    waStatus.status,
  );
  const totalPages = Math.max(1, Math.ceil(totalEnvios / PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* ── Card de estado ───────────────────────────────── */}
      <div
        className={`rounded-2xl border p-5 ${statusBg[waStatus.status] || statusBg.disconnected}`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {isConnected ? (
              <div className="relative p-3 bg-emerald-100 rounded-xl">
                <MessageCircle className="h-7 w-7 text-emerald-600" />
                <span className="absolute top-0 right-0 translate-x-1 -translate-y-1 block h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-white" />
              </div>
            ) : (
              <div className="p-3 bg-gray-100 rounded-xl">
                <MessageCircle className="h-7 w-7 text-gray-400" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-gray-800">
                  Estado de WhatsApp
                </h2>
                {isConnecting && (
                  <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                )}
              </div>
              <p
                className={`text-sm font-medium ${statusColor[waStatus.status] || "text-gray-500"}`}
              >
                {statusLabel[waStatus.status] || waStatus.status}
              </p>
              {isConnected && waStatus.phoneNumber && (
                <p className="text-xs text-gray-500 mt-0.5">
                  +{waStatus.phoneNumber}
                  {waStatus.profileName ? ` · ${waStatus.profileName}` : ""}
                </p>
              )}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={fetchStatus}
              disabled={actionLoading}
              title="Actualizar estado"
              className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-brand-600 hover:border-brand-200 transition-all"
            >
              <RefreshCcw className="h-4 w-4" />
            </button>

            {!isConnected && !isConnecting && (
              <Button
                onClick={handleInitialize}
                disabled={actionLoading}
                className="gap-2"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Wifi className="h-4 w-4" />
                )}
                Conectar WhatsApp
              </Button>
            )}

            {isConnected && (
              <Button
                variant="outline"
                onClick={handleDisconnect}
                disabled={actionLoading}
                className="gap-2 border-red-200 text-red-600 hover:bg-red-50"
              >
                <WifiOff className="h-4 w-4" />
                Desconectar
              </Button>
            )}

            {isConnecting && (
              <Button variant="outline" disabled className="gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Iniciando…
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── QR code ──────────────────────────────────────── */}
      {waStatus.status === "qr" && waStatus.qr && (
        <div className="rounded-2xl border border-blue-100 bg-white p-6 flex flex-col items-center gap-4 shadow-card">
          <div className="text-center">
            <h3 className="text-base font-semibold text-gray-800">
              Escanea el código QR
            </h3>
            <p className="text-sm text-gray-500 mt-1 max-w-sm">
              Abre WhatsApp en tu teléfono → Dispositivos vinculados → Vincular
              dispositivo → Escanea este código
            </p>
          </div>
          <div className="p-3 border-2 border-blue-200 rounded-2xl bg-white shadow-sm">
            <img
              src={waStatus.qr}
              alt="WhatsApp QR"
              className="w-56 h-56 object-contain"
            />
          </div>
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            El QR se actualiza automáticamente cada pocos segundos
          </p>
        </div>
      )}

      {/* ── Instrucciones cuando está desconectado ─────── */}
      {waStatus.status === "disconnected" && (
        <div className="rounded-2xl border border-brand-100 bg-brand-50/40 p-5">
          <h3 className="font-semibold text-gray-700 mb-2">
            Cómo conectar WhatsApp
          </h3>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-600">
            <li>
              Haz clic en <strong>Conectar WhatsApp</strong>.
            </li>
            <li>
              Espera a que aparezca el código QR (puede tardar unos segundos).
            </li>
            <li>
              Abre WhatsApp en tu teléfono y ve a{" "}
              <strong>Dispositivos vinculados</strong>.
            </li>
            <li>
              Toca <strong>Vincular dispositivo</strong> y escanea el código QR.
            </li>
            <li>
              Una vez vinculado, el estado cambiará a <strong>Conectado</strong>
              .
            </li>
          </ol>
          <p className="text-xs text-gray-400 mt-3">
            La sesión se mantiene activa entre reinicios del servidor. Solo es
            necesario escanear una vez.
          </p>
        </div>
      )}

      {/* ── Historial de envíos ───────────────────────── */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-800">
              Historial de envíos
            </h3>
            <p className="text-xs text-gray-500">
              {totalEnvios} registro{totalEnvios !== 1 ? "s" : ""}
            </p>
          </div>
          {/* Filtros */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-3.5 w-3.5 text-brand-400" />
            <select
              value={filtroTipo}
              onChange={(e) => {
                setFiltroTipo(e.target.value);
                setPageEnvios(1);
              }}
              className="text-sm border border-brand-200 rounded-xl px-3 py-1.5 bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            >
              <option value="">Todo tipo</option>
              <option value="resultado">Resultados</option>
              <option value="factura">Facturas</option>
            </select>
            <select
              value={filtroEstado}
              onChange={(e) => {
                setFiltroEstado(e.target.value);
                setPageEnvios(1);
              }}
              className="text-sm border border-brand-200 rounded-xl px-3 py-1.5 bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            >
              <option value="">Todo estado</option>
              <option value="enviado">Enviados</option>
              <option value="error">Con error</option>
              <option value="pendiente">Pendientes</option>
            </select>
            <button
              onClick={fetchEnvios}
              className="p-1.5 rounded-xl border border-brand-200 bg-brand-50 text-brand-500 hover:text-brand-700 transition-all"
            >
              <RefreshCcw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-brand-100">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-brand-gradient text-white">
                {[
                  "Tipo",
                  "Referencia",
                  "Número",
                  "Estado",
                  "Enviado por",
                  "Fecha",
                ].map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${i === 0 ? "text-left" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-50">
              {loadingEnvios ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center">
                    <div className="flex flex-col items-center gap-2 text-brand-400">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="text-sm">Cargando historial…</span>
                    </div>
                  </td>
                </tr>
              ) : envios.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="py-10 text-center text-gray-400 italic text-sm"
                  >
                    No hay envíos registrados.
                  </td>
                </tr>
              ) : (
                envios.map((e, i) => (
                  <tr
                    key={e.id}
                    className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}
                  >
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          e.tipo === "resultado"
                            ? "bg-sky-100 text-sky-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {e.tipo === "resultado" ? "Resultado" : "Factura"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">
                      {e.tipo === "resultado"
                        ? `${e.paciente_cod} / ${e.examen_cod}`
                        : e.factura_id}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {e.numero_destino}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${estadoBadge[e.estado] || "bg-gray-100 text-gray-600"}`}
                      >
                        {e.estado === "enviado" && (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                        {e.estado === "error" && (
                          <XCircle className="h-3 w-3" />
                        )}
                        {e.estado === "pendiente" && (
                          <Clock className="h-3 w-3" />
                        )}
                        {e.estado}
                      </span>
                      {e.error_msg && (
                        <p
                          className="text-xs text-red-500 mt-0.5 max-w-[200px] truncate"
                          title={e.error_msg}
                        >
                          {e.error_msg}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {e.enviado_por || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {e.fecha_envio
                        ? new Date(e.fecha_envio).toLocaleString("es-CO", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación historial */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {envios.length} de {totalEnvios}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="p-1.5 rounded-lg border border-brand-200 disabled:opacity-40 hover:bg-brand-50 transition-all"
                disabled={pageEnvios <= 1}
                onClick={() => setPageEnvios((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3 py-1.5 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 font-medium">
                {pageEnvios}/{totalPages}
              </span>
              <button
                className="p-1.5 rounded-lg border border-brand-200 disabled:opacity-40 hover:bg-brand-50 transition-all"
                disabled={pageEnvios >= totalPages}
                onClick={() => setPageEnvios((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Estadísticas rápidas ────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          {
            label: "Total enviados",
            filter: { estado: "enviado" },
            icon: Send,
            color: "text-emerald-600",
            bg: "bg-emerald-50",
          },
          {
            label: "Con error",
            filter: { estado: "error" },
            icon: XCircle,
            color: "text-red-600",
            bg: "bg-red-50",
          },
          {
            label: "Resultados enviados",
            filter: { tipo: "resultado", estado: "enviado" },
            icon: CheckCircle2,
            color: "text-sky-600",
            bg: "bg-sky-50",
          },
        ].map((stat) => (
          <button
            key={stat.label}
            onClick={() => {
              if (stat.filter.tipo !== undefined)
                setFiltroTipo(stat.filter.tipo);
              if (stat.filter.estado !== undefined)
                setFiltroEstado(stat.filter.estado);
              setPageEnvios(1);
            }}
            className="bg-white border border-brand-100 rounded-xl p-4 text-left hover:border-brand-300 transition-all"
          >
            <div className={`inline-flex p-2 rounded-lg ${stat.bg} mb-2`}>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </div>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default WhatsAppPage;
